import React, { useEffect } from "react";
import HomeHero from "@/components/home/HomeHero";
import Marquee from "@/components/shared/Marquee";
import TrustedByBrandsSection from "@/components/home/TrustedByBrandsSection";
import SelectedWorkSection from "@/components/shared/SelectedWorkSection";
import EventTypesSection from "@/components/home/EventTypesSection";
import WhyPulseSection from "@/components/home/WhyPulseSection";
import PulseMethodSection from "@/components/home/PulseMethodSection";
import ConceptTerritoriesSection from "@/components/home/ConceptTerritoriesSection";
import LeadCaptureSection from "@/components/home/LeadCaptureSection";
import AboutSection from "@/components/home/AboutSection";
import FinalCTASection from "@/components/home/FinalCTASection";

// Mobile-optimized variants (rendered below 768px only).
import MobileSelectedWorkCarousel from "@/components/home/mobile/MobileSelectedWorkCarousel";
import EarlyConversionStrip from "@/components/home/mobile/EarlyConversionStrip";
import MobilePulseMethodTimeline from "@/components/home/mobile/MobilePulseMethodTimeline";
import MobileConceptTerritoriesCarousel from "@/components/home/mobile/MobileConceptTerritoriesCarousel";
import MobileStickyCTA from "@/components/home/mobile/MobileStickyCTA";
import MobileEventTypesCarousel from "@/components/home/mobile/MobileEventTypesCarousel";

/**
 * Homepage journey.
 *
 * Both experiences share the same DOM tree, but section ORDER differs
 * between desktop and mobile via Tailwind's `order-*` / `md:order-*`
 * utilities. Sections that need distinct mobile vs desktop presentations
 * render their mobile variant with `md:hidden` and their desktop variant
 * with `hidden md:block`, so each viewport sees exactly one implementation
 * of each idea.
 *
 * Desktop journey (>= 768px), unchanged:
 *  1.  Hero
 *  2.  Marquee
 *  3.  Trusted brands + metrics
 *  4.  Selected Work
 *  5.  Positioning / Why Pulse
 *  6.  What We Create
 *  7.  Pulse Method
 *  8.  Why Clients Choose Pulse
 *  9.  Creative Territories
 * 10.  Start A Project (Lead Form)
 * 11.  About Pulse
 * 12.  REFORM Ecosystem
 * 13.  Final CTA
 *
 * Mobile journey (< 768px), lead-form-first with progressive disclosure:
 *  1.  Hero
 *  2.  Trusted brands + metrics
 *  3.  Selected Work (touch carousel)
 *  4.  Early conversion CTA strip
 *  5.  What We Create (compact collapsed cards)
 *  6.  Pulse Method (compact timeline)
 *  7.  Why Clients Choose Pulse (3 + View All Six)
 *  8.  Creative Territories (touch carousel)
 *  9.  Lead form
 *  10. Positioning
 *  11. About Pulse
 *  12. REFORM Ecosystem
 *  13. Final CTA
 *  Footer sits below via App shell.
 */
export default function HomePage() {
    useEffect(() => {
        document.title = "REFORM Pulse | Culture-Led Experiential Agency Malaysia";
        const desc = document.querySelector('meta[name="description"]');
        const descText = "REFORM Pulse creates culture-led brand launches, activations, pop-ups, creator events and experiential campaigns in Malaysia and Southeast Asia.";
        if (desc) {
            desc.setAttribute("content", descText);
        } else {
            const meta = document.createElement("meta");
            meta.name = "description";
            meta.content = descText;
            document.head.appendChild(meta);
        }
    }, []);

    return (
        <div className="relative flex flex-col">
            {/* 1. Hero — both experiences */}
            <div className="order-1 md:order-1"><HomeHero /></div>

            {/* 2. Marquee — mobile is content-lean so we hide the marquee
                     transition on small screens; desktop keeps it. */}
            <div className="order-2 md:order-2 hidden md:block"><Marquee /></div>

            {/* 3. Trusted Brands + Metrics — both experiences */}
            <div className="order-3 md:order-3"><TrustedByBrandsSection /></div>

            {/* 4. Selected Work — desktop rows (hidden on mobile via internal class) */}
            <div className="order-4 md:order-4"><SelectedWorkSection /></div>

            {/* 4m. Selected Work — mobile carousel (mobile only) */}
            <div className="order-5 md:order-4 md:hidden"><MobileSelectedWorkCarousel /></div>

            {/* 5m. Early conversion CTA — mobile only, placed right after Selected Work */}
            <div className="order-6 md:hidden"><EarlyConversionStrip /></div>

            {/* 6. What We Create — desktop grid + side-drawer */}
            <div className="order-7 md:order-5"><EventTypesSection /></div>

            {/* 6m. What We Create — mobile carousel (mobile only) */}
            <div className="order-7 md:order-5 md:hidden"><MobileEventTypesCarousel /></div>

            {/* 7. NEW consolidated Why Pulse (replaces old Positioning +
                    Differentiators + Ecosystem card grid). Renders identically
                    on desktop and mobile. */}
            <div className="order-8 md:order-6"><WhyPulseSection /></div>

            {/* 8. Pulse Method (now 4 steps) */}
            <div className="order-9 md:order-7"><PulseMethodSection /></div>
            <div className="order-9 md:order-7 md:hidden"><MobilePulseMethodTimeline /></div>

            {/* 9. Creative Territories */}
            <div className="order-10 md:order-8"><ConceptTerritoriesSection /></div>
            <div className="order-10 md:order-8 md:hidden"><MobileConceptTerritoriesCarousel /></div>

            {/* 10. Lead form */}
            <div className="order-11 md:order-9"><LeadCaptureSection /></div>

            {/* Post-form supporting content (About + Final CTA). The old
                standalone "REFORM Ecosystem" card grid section has been
                removed from the homepage per the consolidation brief; its
                message now lives in the compact ecosystem strip inside
                Why Pulse. The full EcosystemSection component and its
                constants remain in the codebase for possible use on the
                About page. */}
            <div className="order-[21] md:order-10"><AboutSection /></div>
            <div className="order-[23] md:order-11"><FinalCTASection /></div>

            {/* Fixed sticky mobile CTA — outside the ordering flow. */}
            <MobileStickyCTA />
        </div>
    );
}
